import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import '@/module/plan/tracking/events.quiz-plan-expiration';
import { trackEvent } from '@zep/logger/tracking';
import type { WorkspaceDetailMemberType } from '@/api/gen/_model/workspaceDetailMemberType';
import { useUser } from '@/lib/auth';
import { AlertBanner } from '@/shared/components';
import { resolveUpsellingPlanState } from '@/shared/components/UpsellingModal/upsellingPlanState';
import { type WorkspaceScope } from '@/types';
import { Button } from '@/ui_v3';
import { resolveExpirationAlertCta } from '../utils/resolveExpirationAlertCta';
interface PlanExpirationAlertBannerProps {
  alertType: 'D-7' | 'D-3' | 'D-day';
  onClose: () => void;
  workspaceMemberType: WorkspaceDetailMemberType;
  daysUntilExpiry: number;
}
type WorkspaceMemberType = Exclude<WorkspaceDetailMemberType, 'UNKNOWN'>;

/** 서버가 새 scope 값을 추가해도 unknown 으로 떨어뜨린다 — trackEvent 는 런타임 검증이 없다. */
const toTrackedScope = (scope: WorkspaceScope | undefined): 'organization' | 'personal' | 'unknown' => {
  if (scope === 'PERSONAL') return 'personal';
  if (scope === 'ORGANIZATION') return 'organization';
  return 'unknown';
};

/** Cross-origin iframe 접근 오류도 임베드로 처리한다. */
const isEmbedded = () => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

/**
 * 플랜 만료 알림 배너 (AlertBanner 래퍼)
 * D-7, D-3, D-day 시점의 알림 메시지를 정의하고 AlertBanner로 렌더링합니다.
 * CTA 는 개인/단체 워크스페이스에 따라 플랜 관리 또는 국가별 문의 링크로 갈립니다.
 */
export const PlanExpirationAlertBanner = ({
  alertType,
  onClose,
  daysUntilExpiry,
  workspaceMemberType
}: PlanExpirationAlertBannerProps) => {
  const {
    t
  } = useTranslation(['common']);
  const {
    user
  } = useUser();
  const router = useRouter();
  const ALERT_MESSAGES: Record<'D-7' | 'D-3' | 'D-day', Record<WorkspaceMemberType, string>> = {
    'D-7': {
      OWNER: t('common:alert.owner.expiredPlanLevel1', {
        n: daysUntilExpiry
      }),
      MEMBER: t('common:alert.member.expiredPlanLevel1', {
        n: daysUntilExpiry
      })
    },
    'D-3': {
      OWNER: t('common:alert.owner.expiredPlanLevel2', {
        n: daysUntilExpiry
      }),
      MEMBER: t('common:alert.member.expiredPlanLevel1', {
        n: daysUntilExpiry
      })
    },
    'D-day': {
      OWNER: t('common:alert.owner.expiredPlanLevel3'),
      MEMBER: t('common:alert.member.expiredPlanLevel3')
    }
  };
  const planState = resolveUpsellingPlanState(user);
  const cta = resolveExpirationAlertCta({
    workspaceMemberType,
    canSelfServeExtend: planState.kind === 'individual',
    countryCode: user?.countryCode
  });
  const handleCtaClick = () => {
    if (!cta) return;
    trackEvent('quiz_plan_expiration_alert_cta_click', {
      alert_type: alertType,
      workspace_scope: toTrackedScope(user?.workspace?.scope),
      destination: cta.kind === 'manage' ? 'plan_management' : 'inquiry',
      days_until_expiry: daysUntilExpiry,
      plan_state: planState.kind === 'anonymous' ? 'unknown' : planState.kind
    });

    // 임베드 호스트가 /profile/plan 으로 대체되지 않게 한다.
    if (cta.kind === 'manage' && !isEmbedded()) {
      // 취소된 이동에서는 onClose가 배너를 영구 숨기지 않게 한다.
      router.push(cta.href).then(navigated => {
        if (navigated) onClose();
      },
      // 취소된 라우팅은 reject되므로 배너를 유지한다.
      () => {});
      return;
    }
    window.open(cta.href);
    onClose();
  };
  return <AlertBanner type={alertType === 'D-day' ? 'error' : 'warning'} rightSlot={cta && <Button variant={'contained'} intent={'gradient'} size={'sm'} onClick={handleCtaClick}>
            {t(cta.kind === 'manage' ? 'common:planManage' : 'common:alert.inquiredExtendPlan')}
          </Button>} title={ALERT_MESSAGES[alertType][workspaceMemberType as WorkspaceMemberType]} onClose={onClose} datatype="plan-expiration-alert" data-sentry-element="AlertBanner" data-sentry-component="PlanExpirationAlertBanner" data-sentry-source-file="PlanExpirationAlertBanner.tsx" />;
};